import { Capacitor } from '@capacitor/core';

/**
 * Reads a URL from the environment, or falls back to a default.
 *
 * A missing or malformed variable must fail loudly here, not produce a `fetch`
 * to `undefined/auth/login` three screens later.
 *
 * Warning: **This module deliberately does not use zod.** It is loaded on the very
 * first render — the API address is needed before any call — so importing zod
 * put it on the critical path on its own: **18.6 Ko gzip of a 160 Ko budget**,
 * measured, to check two strings. Forms keep zod: they live in lazily loaded
 * chunks where it is already paid for.
 *
 * The guarantee is unchanged: an absent value takes the default, a present but
 * invalid one throws at start-up.
 *
 * @param value The raw environment value.
 * @param fallback Used when the value is absent or empty.
 * @returns The validated URL.
 * @throws Error when the value is present but not a valid URL.
 */
function readUrl(value: unknown, fallback: string): string {
  if (value === undefined || value === '') return fallback;
  if (typeof value !== 'string') {
    throw new Error(
      `Invalid configuration (.env): VITE_API_URL is not a string.`,
    );
  }
  try {
    // `new URL` is the browser's own URL validation: stricter than a
    // hand-written regular expression, and free.
    new URL(value);
  } catch {
    throw new Error(
      `Invalid configuration (.env): VITE_API_URL is not a valid URL ("${value}").`,
    );
  }
  return value;
}

function readString(value: unknown, fallback: string): string {
  return typeof value === 'string' && value !== '' ? value : fallback;
}

const raw = {
  VITE_API_URL: readUrl(import.meta.env.VITE_API_URL, 'http://localhost:3000'),
  VITE_APP_NAME: readString(import.meta.env.VITE_APP_NAME, 'Meeply'),
};

/**
 * Hosts that can only mean "the machine this is running on".
 *
 * `localhost`, the loopback addresses, and any bare IPv4 — a laptop's address
 * on its own network. A name with a dot and letters is a real domain, and a
 * real domain was chosen on purpose.
 */
const DEVELOPMENT_HOST =
  /^(localhost|127\.0\.0\.1|\[?::1\]?|\d{1,3}(\.\d{1,3}){3})$/;

/**
 * Resolves the API host for the current platform.
 *
 * Warning: When the frontend is served by one host and the API by another,
 * `SameSite` cookies never come back, and the symptom — "session lost on
 * reload" — does not point at its cause. The frozen frontend lost days to
 * exactly this.
 *
 * - **Web, in development**: the configured host is `localhost` or a LAN IP,
 *   and it is realigned onto whichever host actually served the page. Opening
 *   the app on `localhost` or on a phone over the network then works with no
 *   reconfiguration, port and scheme kept.
 * - **Web, in production**: the configured host is a real domain —
 *   `api.meeply.tv` — and it is left alone. ⚠️ Realigning it there pointed
 *   every call at `meeply.tv`, which serves files and answered **405** to the
 *   first POST. The two hosts are a decision, not an accident: they share a
 *   registrable domain, so the cookie is same-site and comes back, and the API
 *   already names the site in `FRONTEND_ORIGIN` for CORS.
 * - **Native (Capacitor)**: the origin is `capacitor://localhost`, so there is
 *   nothing to align and the configured URL is taken as-is. It is also why
 *   native auth cannot rely on an httpOnly cookie.
 *
 * @returns The base URL every request is built on.
 */
function resolveApiBaseUrl(): string {
  if (Capacitor.isNativePlatform() || typeof window === 'undefined') {
    return raw.VITE_API_URL;
  }
  try {
    const configured = new URL(raw.VITE_API_URL);
    if (!DEVELOPMENT_HOST.test(configured.hostname)) {
      return configured.origin;
    }
    configured.hostname = window.location.hostname;
    return configured.origin;
  } catch {
    return raw.VITE_API_URL;
  }
}

export const env = {
  appName: raw.VITE_APP_NAME,
  /** Injected at build time from `package.json` (see `vite.config.ts`). */
  appVersion: __APP_VERSION__,
  apiBaseUrl: resolveApiBaseUrl(),
  isNative: Capacitor.isNativePlatform(),
  platform: Capacitor.getPlatform(),
  isDev: import.meta.env.DEV,
} as const;
